import React, { useCallback, useState } from 'react';
import { hostProfileApi } from '../services/hostProfileApi.ts';

interface HostProfileActionsProps {
  hostId: string;
  hostTitle?: string;
}

export const HostProfileActions: React.FC<HostProfileActionsProps> = ({ hostId, hostTitle }) => {
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastMessage, setLastMessage] = useState<string | null>(null);
  const [allowWeak, setAllowWeak] = useState(false);

  const run = useCallback(async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError(null);
    setLastMessage(null);
    try {
      const out = await fn();
      setLastMessage(JSON.stringify(out, null, 2));
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setBusy(null);
    }
  }, []);

  const validation = preview?.validation as Record<string, unknown> | undefined;
  const vStatus = validation?.status as string | undefined;

  return (
    <div className="host-profile-actions">
      <h4 className="host-profile-actions-title">Pipeline actions</h4>
      <p className="host-profile-actions-sub">
        Uses the same backend as <code>run_host_profile.py</code> on the scraper API (Cloud Run or local server).
        Defaults favor <strong>dry-run</strong>; writes need an explicit confirmation step.
      </p>

      <div className="host-profile-actions-buttons">
        <button
          type="button"
          className="hosts-refresh-btn"
          disabled={!!busy}
          onClick={() =>
            run('preview', () =>
              hostProfileApi.preview({ host_id: hostId, sample_events: 5 }).then((p) => {
                setPreview(p);
                return p;
              }),
            )
          }
        >
          {busy === 'preview' ? 'Generating…' : 'Generate preview'}
        </button>
        <label className="host-profile-weak-label">
          <input type="checkbox" checked={allowWeak} onChange={(e) => setAllowWeak(e.target.checked)} />
          Allow weak preview on writes
        </label>
      </div>

      {preview && (
        <div className="host-profile-preview-summary">
          <div>
            <strong>Preview</strong> for {hostTitle || hostId}
            {vStatus && (
              <span className={`host-profile-val-status status-${vStatus}`}> · validation: {vStatus}</span>
            )}
          </div>
          <div className="host-profile-preview-fields">
            <span>title: {(preview.title as string) || '—'}</span>
            <span>
              url:{' '}
              {(preview.website_url as string) ||
                (preview.url as string) ||
                '—'}
            </span>
          </div>
        </div>
      )}

      <div className="host-profile-actions-row">
        <button
          type="button"
          className="hosts-refresh-btn"
          disabled={!!busy || !preview}
          onClick={() =>
            run('validate', () => hostProfileApi.validatePreview(preview!).then((r) => ({ validate: r })))
          }
        >
          Validate JSON
        </button>
        <button
          type="button"
          className="hosts-refresh-btn"
          disabled={!!busy || !preview}
          onClick={() =>
            run('upsert-dry', () =>
              hostProfileApi.upsert({
                preview: preview!,
                host_id: hostId,
                dry_run: true,
                allow_weak_preview: allowWeak,
              }),
            )
          }
        >
          Upsert dry-run
        </button>
        <button
          type="button"
          className="host-profile-btn-danger"
          disabled={!!busy || !preview}
          onClick={() => {
            if (
              !window.confirm(
                'Write host document to Firestore (upsert)? Refused if validation does not pass unless “allow weak” is checked.',
              )
            ) {
              return;
            }
            run('upsert', () =>
              hostProfileApi.upsert({
                preview: preview!,
                host_id: hostId,
                dry_run: false,
                allow_weak_preview: allowWeak,
              }),
            );
          }}
        >
          Upsert (write)
        </button>
      </div>

      <div className="host-profile-actions-row">
        <button
          type="button"
          className="hosts-refresh-btn"
          disabled={!!busy || !preview}
          onClick={() =>
            run('apply-dry', () =>
              hostProfileApi.applyPreview({
                preview: preview!,
                host_id: hostId,
                dry_run: true,
                allow_weak_preview: allowWeak,
              }),
            )
          }
        >
          Apply preview + link (dry-run)
        </button>
        <button
          type="button"
          className="host-profile-btn-danger"
          disabled={!!busy || !preview}
          onClick={() => {
            if (
              !window.confirm(
                'Apply preview: upsert host and link matching posts (upcoming by default). Continue?',
              )
            ) {
              return;
            }
            run('apply', () =>
              hostProfileApi.applyPreview({
                preview: preview!,
                host_id: hostId,
                dry_run: false,
                allow_weak_preview: allowWeak,
              }),
            );
          }}
        >
          Apply preview + link (write)
        </button>
      </div>

      <div className="host-profile-actions-row">
        <button
          type="button"
          className="hosts-refresh-btn"
          disabled={!!busy}
          onClick={() =>
            run('link-ov-dry', () =>
              hostProfileApi.linkOverrides({ host_id: hostId, dry_run: true }),
            )
          }
        >
          Link overrides (dry-run)
        </button>
        <button
          type="button"
          className="host-profile-btn-danger"
          disabled={!!busy}
          onClick={() => {
            if (
              !window.confirm(
                'Link upcoming posts using host_id_overrides for this host? Recommended after overrides exist in config.',
              )
            ) {
              return;
            }
            run('link-ov', () =>
              hostProfileApi.linkOverrides({ host_id: hostId, dry_run: false }),
            );
          }}
        >
          Link overrides (write)
        </button>
      </div>

      {error && <div className="host-profile-error">{error}</div>}
      {lastMessage && (
        <details className="host-profile-result">
          <summary>Last API result</summary>
          <pre>{lastMessage}</pre>
        </details>
      )}
    </div>
  );
};
